import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer'
import React, { FC } from 'react'

import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCustomSection } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanExecutiveSummarySection } from 'api/interfaces/MasterCampaignPlanExecutiveSummarySection'
import { MasterCampaignPlanCampaignBuilder } from 'api/interfaces/MasterCampaignStub'

import { getPlanDescription, getChannelUtilizationContent, getBudgetContent } from '../../utils/planContentUtils'

Font.register({
  family: 'myriad-pro',
  fonts: [
    {
      src: require('../../../../../../assets/fonts/myriad-pro-regular.woff'),
      fontWeight: 400
    },
    {
      src: require('../../../../../../assets/fonts/myriad-pro-bold.woff'),
      fontWeight: 700
    }
  ]
})

Font.registerHyphenationCallback(word => [word])

interface ExecutiveSummaryPdfProps {
  masterCampaignPlan: MasterCampaignPlan
  campaigns: MasterCampaignPlanCampaignBuilder[]
  accountName: string
  ganttChartImage?: string
  executiveSummarySection?: MasterCampaignPlanExecutiveSummarySection
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: '1.25in',
    paddingLeft: '1.75in',
    paddingBottom: '1.25in',
    paddingRight: '1.75in',
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  header: {
    fontFamily: 'myriad-pro',
    fontSize: '26pt',
    lineHeight: 1.2,
    marginBottom: '10pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  section: {
    marginBottom: '4pt'
  },
  sectionTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '17pt',
    lineHeight: 1.2,
    marginTop: '30pt',
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  sectionSubtitle: {
    fontFamily: 'myriad-pro',
    fontSize: '15pt',
    lineHeight: 1.2,
    marginTop: '30pt',
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  ganttPage: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: '1.75in',
    paddingLeft: '0in',
    paddingBottom: '1.75in',
    paddingRight: '0in',
    fontFamily: 'myriad-pro',
    justifyContent: 'center',
    alignItems: 'center'
  },
  ganttImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  ganttImageContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    transform: 'rotate(-90deg)'
  },
  planDescription: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  }
})

const ExecutiveSummaryPdf: FC<ExecutiveSummaryPdfProps> = ({ masterCampaignPlan, campaigns, accountName, ganttChartImage, executiveSummarySection }) => {
  const planDescription = getPlanDescription(accountName)
  const channelContent = getChannelUtilizationContent(campaigns)
  const budgetContent = getBudgetContent(campaigns, false)

  const renderCustomSections = (sections: MasterCampaignPlanCustomSection[] | undefined) => {
    if (!sections || sections.length === 0) return null

    return sections.map((section, index) => (
      <View key={index} style={styles.section}>
        {section.showTitle && section.title && <Text style={styles.sectionTitle}>{section.title}</Text>}
        <Text style={styles.planDescription}>{section.content}</Text>
      </View>
    ))
  }

  return (
    <Document>
      {ganttChartImage && (
        <Page size='LETTER' style={styles.ganttPage}>
          <View style={styles.ganttImageContainer}>
            <Image src={ganttChartImage} style={styles.ganttImage} />
          </View>
        </Page>
      )}
      <Page size='LETTER' style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.header}>{executiveSummarySection?.pageTitle || 'Executive Summary'}</Text>
        </View>

        {executiveSummarySection?.introduction?.hideAll !== true && (
          <View style={styles.section}>
            <Text style={styles.planDescription}>{executiveSummarySection?.introduction?.content || planDescription.combined}</Text>
          </View>
        )}

        {renderCustomSections(executiveSummarySection?.aboveCampaignTimeline)}

        {renderCustomSections(executiveSummarySection?.aboveChannelUtilization)}

        {executiveSummarySection?.channelUtilization?.hideAll !== true && channelContent && (
          <View style={styles.section}>
            {executiveSummarySection?.channelUtilization?.showTitle !== false && <Text style={styles.sectionTitle}>{executiveSummarySection?.channelUtilization?.title || 'Channel Utilization'}</Text>}
            <Text style={styles.planDescription}>{executiveSummarySection?.channelUtilization?.content || channelContent}</Text>
          </View>
        )}

        {renderCustomSections(executiveSummarySection?.aboveAudienceAndGeoSummary)}

        {executiveSummarySection?.audienceStrategy?.hideAll !== true && (
          <View style={styles.section}>
            {executiveSummarySection?.audienceStrategy?.showTitle !== false && <Text style={styles.sectionTitle}>{executiveSummarySection?.audienceStrategy?.title || 'Audience Strategy'}</Text>}
            <Text style={styles.planDescription}>{executiveSummarySection?.audienceStrategy?.content || masterCampaignPlan.audienceSummary}</Text>
          </View>
        )}

        {executiveSummarySection?.geographicTargeting?.hideAll !== true && (
          <View style={styles.section}>
            {executiveSummarySection?.geographicTargeting?.showTitle !== false && <Text style={styles.sectionTitle}>{executiveSummarySection?.geographicTargeting?.title || 'Geographic Targeting'}</Text>}
            <Text style={styles.planDescription}>{executiveSummarySection?.geographicTargeting?.content || masterCampaignPlan.geoSummary}</Text>
          </View>
        )}

        {renderCustomSections(executiveSummarySection?.aboveBudget)}

        {executiveSummarySection?.budget?.hideAll !== true && (
          <View style={styles.section}>
            {executiveSummarySection?.budget?.showTitle !== false && <Text style={styles.sectionTitle}>{executiveSummarySection?.budget?.title || 'Budget'}</Text>}
            <Text style={styles.planDescription}>{executiveSummarySection?.budget?.content || budgetContent}</Text>
          </View>
        )}

        {renderCustomSections(executiveSummarySection?.aboveBottom)}
      </Page>
    </Document>
  )
}

export default ExecutiveSummaryPdf
